import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseLocalDate, todayLocal, type YMD } from "./dates";
import { pregnancy, type DueMode } from "./pregnancy";
import { NoticeBox } from "@/components/NoticeBox";

const fmt = (d: YMD) => new Date(d.y, d.m - 1, d.d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

const TOOL_INFO_CONTENT = {
  what: `Estimates a pregnancy due date from the first day of your last menstrual period (the standard 280-day method, adjusted for your cycle length), or from a known conception date or IVF embryo transfer date, and shows how far along you are.`,
  how: `Choose what you know: your last period, a conception date, or an IVF transfer date. Enter the date and, for a last period, your usual cycle length if it is not 28 days. The due date, progress and trimester dates are calculated.`,
  tip: `This is a standard estimate used as a starting reference point — an ultrasound-based estimate from your care provider is generally more accurate for your actual situation, especially if your cycle isn't a typical 28 days.`,
};

const SEL = "border rounded px-2 py-2 w-full bg-background text-sm";
const LABEL: Record<DueMode, string> = { lmp: "First day of last menstrual period (LMP)", conception: "Conception date", ivf5: "IVF transfer date (day-5 embryo)", ivf3: "IVF transfer date (day-3 embryo)" };

export default function PregnancyDueDateCalculator() {
  const [mode, setMode] = useState<DueMode>("lmp");
  const [dateText, setDateText] = useState("");
  const [cycleText, setCycleText] = useState("28");

  const date = parseLocalDate(dateText);
  const cycle = Math.round(Number(cycleText));
  const cycleOk = cycle >= 21 && cycle <= 45;
  const p = date && cycleOk ? pregnancy(mode, date, todayLocal(), cycle) : null;
  const { blocked } = useLogToolUsage(true, 3000);
  const showProgress = p && p.state === "ok";

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <div><Label>I know my…</Label>
          <select className={SEL} aria-label="Date type" value={mode} onChange={(e) => setMode(e.target.value as DueMode)}>
            <option value="lmp">Last menstrual period</option><option value="conception">Conception date</option><option value="ivf5">IVF transfer (day-5 embryo)</option><option value="ivf3">IVF transfer (day-3 embryo)</option>
          </select></div>
        <div className="grid grid-cols-3 gap-3">
          <div className={mode === "lmp" ? "col-span-2" : "col-span-3"}><Label>{LABEL[mode]}</Label><Input type="date" aria-label="Date" value={dateText} onChange={(e) => setDateText(e.target.value)} /></div>
          {mode === "lmp" && <div><Label>Cycle (days)</Label><Input inputMode="numeric" aria-label="Cycle length" value={cycleText} onChange={(e) => setCycleText(e.target.value)} /></div>}
        </div>
        {mode === "lmp" && !cycleOk && <p className="text-sm text-red-600" data-testid="pd-cycle-error">Enter a cycle length between 21 and 45 days.</p>}
        {p && p.state === "future" && <p className="text-sm text-red-600" data-testid="pd-future">That date is in the future. Enter a date that has already happened.</p>}
        {p && p.state === "overdue" && <NoticeBox className="text-sm" testId="pd-overdue">This date is more than 42 weeks ago, so no due date is shown. Please check it.</NoticeBox>}

        {p && p.state === "ok" && !blocked ? (
          <div className="space-y-3">
            <div className="p-4 rounded-xl border bg-primary/5 text-center">
              <p className="text-xs text-muted-foreground">Estimated Due Date</p>
              <p className="text-xl font-bold text-primary" data-testid="pd-due">{fmt(p.due)}</p>
            </div>
            {showProgress && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Current Progress</p><p className="text-sm font-bold" data-testid="pd-progress">{p.weeks} week{p.weeks === 1 ? "" : "s"} {p.days} day{p.days === 1 ? "" : "s"}</p></div>
                  <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Trimester</p><p className="text-sm font-bold">{p.trimester}</p></div>
                </div>
                <p className="text-xs text-center text-muted-foreground">First trimester ends {fmt(p.firstTrimesterEnds)} · second trimester ends {fmt(p.secondTrimesterEnds)}</p>
              </>
            )}
            <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Estimated Conception Date</p><p className="text-sm font-bold" data-testid="pd-conception">{fmt(p.conception)}</p></div>
          </div>
        ) : !p ? (
          <p className="text-sm text-center text-muted-foreground">Enter a date to calculate.</p>
        ) : null}

        <p className="text-xs text-muted-foreground text-center">
          Based on the standard 280-day (40-week) estimate. Actual due dates vary: your healthcare provider's estimate (especially from an early ultrasound) is more accurate than this calculation alone.
        </p>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
